import styled from "@emotion/styled";
import {
  GroupAddSharp,
  PaymentOutlined,
  PaymentRounded,
  PaymentsSharp,
  Polyline,
} from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import KeyIcon from "@mui/icons-material/VpnKey";
import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/Info";
import Tooltip from "@mui/material/Tooltip";
import { ReactComponent as AccountList } from "../assets/icons/accountList.svg";
import { ReactComponent as Clone } from "../assets/icons/clone.svg";
import { ReactComponent as Confirm } from "../assets/icons/confirm.svg";
import { ReactComponent as Details } from "../assets/icons/details.svg";
import { ReactComponent as IP } from "../assets/icons/ip.svg";
import { ReactComponent as Play } from "../assets/icons/play.svg";
import { IAction } from "../types/table/interfaces.type";
import { getDisabledActionProps } from "../utils/helpers";
import TerminalIcon from "@mui/icons-material/Terminal";
import { COLORS } from "../assets/theme";
import HistoryIcon from "@mui/icons-material/History";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import DownloadIcon from "@mui/icons-material/Download";
import invoiceIcon from "../assets/icons/invoice-icon.png";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { color } from "@mui/system";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ContactIcon from "@mui/icons-material/ContactMail";

interface ActionParams {
  id: string | number;
}
const StyledCloneIcon = styled(Clone)(
  ({ disabled }: { disabled: boolean }) => ({
    color: disabled ? "#D3D3D3" : "#A39455",
    width: "24px",
    height: "24px",
  })
);

const StyledAccountListIcon = styled(AccountList)(
  ({ disabled }: { disabled: boolean }) => ({
    color: disabled ? "#D3D3D3" : "#A39455",
    width: "24px",
    height: "24px",
  })
);

const StyledIPIcon = styled(IP)(({ disabled }: { disabled: boolean }) => ({
  color: disabled ? "#D3D3D3" : "#A39455",
  width: "24px",
  height: "24px",
}));

interface ActionsProps {
  handleOpenEditModal?: {
    handler: (id: string | number) => void;
    isDisabled?: boolean | ((row: any) => boolean);
    onContextMenu?: (event: React.MouseEvent, id: string | number) => void;
  };
  onChangePassword?: {
    handler: (id: string | number) => void;
    isDisabled?: boolean | ((row: any) => boolean);
  };
  onDelete?: {
    handler: (id: string | number) => void;
    isDisabled?: boolean;
  };
  onParentIcon?: {
    handler: (id: string | number) => void;
    isDisabled?: boolean;
  };
  onViewHistory?: {
    handler: (id: string | number) => void;
    isDisabled?: boolean;
  };
  onListAccounts?: {
    handler: (id: string | number) => void | ((row: any) => boolean);
    isDisabled?: boolean;
  };
  onIPAction?: {
    handler: (id: string | number) => void;
    isDisabled?: boolean;
  };
  onCloneAction?: {
    handler: (id: string | number) => void;
    isDisabled?: boolean;
  };
  onTelnetAction?: {
    handler: (id: string | number) => void | ((row: any) => boolean);
    isDisabled?: boolean;
  };
  onPdf?: {
    handler: (id: string | number) => void;
    isDisabled?: boolean;
  };
  onPdfDetails?: {
    handler: (id: string | number) => void;
    isDisabled?: boolean;
  };
  onExportAttem?: {
    handler: (id: string | number) => void | ((row: any) => boolean);
    isDisabled?: boolean;
  };
  onDetails?: {
    handler: (id: string | number) => void;
    isDisabled?: boolean;
    visible?: (row: any) => boolean | boolean;
  };
  onErrors?: {
    handler: (id: string | number) => void;
    isDisabled?: boolean;
    visible?: (row: any) => boolean | boolean;
  };

  onConfirm?: {
    handler: (id: string | number) => void;
    isDisabled?: boolean | ((row: any) => boolean);
  };
  onPlay?: {
    handler: (id: string | number) => void;
    isDisabled?: boolean | ((row: any) => boolean);
    icon?: (row: any) => React.ReactNode;
  };
  onPolyline?: {
    handler: (id: string | number) => void;
    isDisabled?: boolean | ((row: any) => boolean);
  };
  reGenerate?: {
    handler: (id: string | number) => void;
    isDisabled?: boolean | ((row: any) => boolean);
  };
  onView?: {
    handler: (id: string | number) => void;
    isDisabled?: boolean | ((row: any) => boolean);
    visible?: (row: any) => boolean;
  };
  onContact?: {
    handler: (id: string | number) => void;
    isDisabled?: boolean | ((row: any) => boolean);
  };
  onPay?: {
    handler: (id: string | number) => void;
    isDisabled?: boolean | ((row: any) => boolean);
    visible?: (row: any) => boolean;
  };
  onCancel?: {
    handler: (id: string | number) => void;
    isDisabled?: boolean | ((row: any) => boolean);
    visible?: (row: any) => boolean;
  };
  onApprove?: {
    handler: (id: string | number) => void;
    isDisabled?: boolean | ((row: any) => boolean);
    visible?: (row: any) => boolean;
  };
  onReject?: {
    handler: (id: string | number) => void;
    isDisabled?: boolean | ((row: any) => boolean);
    visible?: (row: any) => boolean;
  };
}

const withMinWait = (
  handler: (params: ActionParams) => void,
  delayTime = 500
) => {
  return async (params: ActionParams): Promise<void> => {
    const handlerPromise = Promise.resolve(handler(params));
    const delayPromise = new Promise<void>((resolve) => {
      setTimeout(() => {
        resolve();
      }, delayTime);
    });
    await Promise.all([handlerPromise, delayPromise]);
  };
};

const Actions = ({
  handleOpenEditModal,
  onChangePassword,
  onDelete,
  onParentIcon,
  onViewHistory,
  onTelnetAction,
  onExportAttem,
  onErrors,
  onListAccounts,
  onIPAction,
  onPdfDetails,
  onCloneAction,
  onPdf,
  onView,
  onContact,
  onPay,
  onCancel,
  onApprove,
  onReject,
  onDetails,
}: ActionsProps): IAction<ActionParams>[] => {
  const actions: IAction<ActionParams>[] = [
    ...(handleOpenEditModal
      ? [
          {
            id: "edit",
            label: "Edit",
            name: "Edit",
            icon: (row: any) => {
              const { color } = getDisabledActionProps(
                row,
                handleOpenEditModal
              );
              return (
                <Tooltip title="Edit">
                  <EditIcon sx={{ color, width: 23 }} />
                </Tooltip>
              );
            },
            handler: ({ id }: ActionParams) => {
              handleOpenEditModal.handler(id);
            },
            onContextMenu: handleOpenEditModal.onContextMenu
              ? (event: React.MouseEvent, row: any) => {
                  handleOpenEditModal.onContextMenu!(event, row.id);
                }
              : undefined,
            disabled: (row: any) =>
              getDisabledActionProps(row, handleOpenEditModal).disabled,
          },
        ]
      : []),
    ...(onViewHistory
      ? [
          {
            id: "viewHistory",
            label: "Transactions",
            name: "Transactions",
            icon: (
              <Tooltip title="Transactions">
                <HistoryIcon sx={{ color: COLORS.MainColor, width: 23 }} />
              </Tooltip>
            ),
            handler: ({ id }: ActionParams) => {
              onViewHistory.handler(id);
            },
            disabled: onViewHistory.isDisabled || false,
          },
        ]
      : []),
    ...(onChangePassword
      ? [
          {
            id: "changePassword",
            label: "Change Password",
            name: "Change Password",
            icon: (row: any) => {
              const { color } = getDisabledActionProps(row, onChangePassword);
              return (
                <Tooltip title="Reset Password">
                  <KeyIcon sx={{ color, width: 23 }} />
                </Tooltip>
              );
            },
            handler: ({ id }: ActionParams) => {
              onChangePassword.handler(id);
            },
          },
        ]
      : []),
    ...(onDelete
      ? [
          {
            id: "delete",
            label: "Delete",
            name: "Delete",
            icon: (
              <Tooltip title="Delete">
                <DeleteIcon
                  sx={{
                    color: onDelete.isDisabled ? "#D3D3D3" : COLORS.MainColor,
                    width: 23,
                  }}
                />
              </Tooltip>
            ),
            handler: ({ id }: ActionParams) => {
              onDelete.handler(id);
            },
            disabled: onDelete.isDisabled || false,
          },
        ]
      : []),
    ...(onPay
      ? [
          {
            id: "pay",
            label: "Pay",
            name: "Pay",
            icon: (row: any) => {
              const { color } = getDisabledActionProps(row, onPay);
              return (
                <Tooltip title="Pay">
                  <PaymentsSharp sx={{ color, width: 23 }} />
                </Tooltip>
              );
            },
            handler: ({ id }: ActionParams) => {
              onPay.handler(id);
            },
            disabled: (row: any) =>
          getDisabledActionProps(row, onPay).disabled,
        visible: onPay.visible,
          },
        ]
      : []),
    ...(onCancel
      ? [
          {
            id: "cancel",
            label: "Cancel",
            name: "Cancel",
            icon: (row: any) => {
              const isDisabled =
                typeof onCancel.isDisabled === "function"
                  ? onCancel.isDisabled(row)
                  : onCancel.isDisabled || false;
              return (
                <Tooltip title="Cancel">
                  <CloseIcon
                    sx={{
                      color: isDisabled ? "#D3D3D3" : "#d32f2f",
                      width: 23,
                    }}
                  />
                </Tooltip>
              );
            },
            handler: ({ id }: ActionParams) => {
              onCancel.handler(id);
            },
            disabled: (row: any) =>
              getDisabledActionProps(row, onCancel).disabled,
            visible: onCancel.visible,
          },
        ]
      : []),
    ...(onView
      ? [
          {
            id: "view",
            label: "View",
            name: "View",
            icon: (row: any) => {
              const { color } = getDisabledActionProps(row, onView);
              return (
                <Tooltip title="View">
                  <VisibilityIcon sx={{ color, width: 23 }} />
                </Tooltip>
              );
            },
            handler: ({ id }: ActionParams) => {
              onView.handler(id);
            },
            disabled: (row: any) =>
              getDisabledActionProps(row, onView).disabled,
            visible: onView.visible,
          },
        ]
      : []),
    ...(onContact
      ? [
          {
            id: "contact",
            label: "Contact",
            name: "Contact",
            icon: (row: any) => {
              const { color } = getDisabledActionProps(row, onContact);
              return (
                <Tooltip title="Contact">
                  <ContactIcon sx={{ color, width: 23 }} />
                </Tooltip>
              );
            },
            handler: ({ id }: ActionParams) => {
              onContact.handler(id);
            },
            disabled: (row: any) =>
              getDisabledActionProps(row, onContact).disabled,
          },
        ]
      : []),
    ...(onParentIcon
      ? [
          {
            id: "parentIcon",
            label: "Parent",
            name: "Parent",
            icon: (
              <Tooltip title="Parent">
                <GroupAddSharp
                  sx={{
                    color: onParentIcon.isDisabled
                      ? "#D3D3D3"
                      : COLORS.MainColor,
                    width: 23,
                  }}
                />
              </Tooltip>
            ),
            handler: ({ id }: ActionParams) => {
              onParentIcon.handler(id);
            },
            disabled: onParentIcon.isDisabled || false,
          },
        ]
      : []),
    ...(onListAccounts
      ? [
          {
            id: "listAccounts",
            label: "List of Accounts",
            name: "List Accounts",
            icon: (
              <Tooltip title="List of Accounts">
                <StyledAccountListIcon
                  disabled={onListAccounts.isDisabled || false}
                  width={23}
                />
              </Tooltip>
            ),
            handler: (rowOrId: any) => {
              if (rowOrId) {
                onListAccounts.handler(rowOrId);
              } else {
                console.warn("Row is missing for List of Accounts action");
              }
            },
            disabled: onListAccounts.isDisabled || false,
          },
        ]
      : []),
    ...(onIPAction
      ? [
          {
            id: "ipAction",
            label: "IP Action",
            name: "IP",
            icon: (
              <Tooltip title="IP Action">
                <StyledIPIcon disabled={onIPAction.isDisabled || false} />
              </Tooltip>
            ),
            handler: ({ id }: ActionParams) => {
              if (id) {
                onIPAction.handler(id);
              } else {
                console.warn("Identifier is missing for IP Action");
              }
            },
            disabled: onIPAction.isDisabled || false,
          },
        ]
      : []),
    ...(onPdf
      ? [
          {
            id: "pdf",
            label: "",
            name: "pdf",
            icon: (
              <Tooltip title="Invoice">
                <img
                  src={invoiceIcon}
                  alt="Invoice"
                  style={{ width: 23, height: 23 }}
                />
              </Tooltip>
            ),
            handler: ({ id }: ActionParams) => {
              const identifier = id;
              if (identifier && !onPdf.isDisabled) {
                onPdf.handler(identifier);
              }
            },
            disabled: onPdf.isDisabled || false,
          },
        ]
      : []),
    ...(onPdfDetails
      ? [
          {
            id: "PdfDetails",
            label: "",
            name: "PdfDetails",
            icon: (
              <Tooltip title="Pdf Details">
                <img
                  src={invoiceIcon}
                  alt="Invoice"
                  style={{ width: 23, height: 23 }}
                />
              </Tooltip>
            ),
            handler: ({ id }: ActionParams) => {
              const identifier = id;
              if (identifier && !onPdfDetails.isDisabled) {
                onPdfDetails.handler(identifier);
              }
            },
            disabled: onPdfDetails.isDisabled || false,
          },
        ]
      : []),
    ...(onExportAttem
      ? [
          {
            id: "Attem",
            label: "",
            name: "Attem",
            icon: (row: any) => {
              const stroke = getDisabledActionProps(row, onExportAttem).stroke;
              return (
                <Tooltip title="Download file">
                  <FileDownloadIcon
                    sx={{ color: stroke, width: 23, height: 30 }}
                  />
                </Tooltip>
              );
            },
            handler: (rowOrId: any) => {
              if (rowOrId) {
                onExportAttem.handler(rowOrId);
              } else {
                console.warn("Row is missing for List of Accounts action");
              }
            },
            disabled: (row: any) =>
              getDisabledActionProps(row, onExportAttem).disabled,
          },
        ]
      : []),
    ...(onApprove
      ? [
          {
            id: "approve",
            label: "Approve",
            name: "Approve",
            icon: (
              <Tooltip title="Approve">
                <CheckCircleIcon sx={{ color: "#2e7d32", width: 23 }} />
              </Tooltip>
            ),
            handler: ({ id }: ActionParams) => {
              onApprove.handler(id);
            },
             disabled: (row: any) =>
          getDisabledActionProps(row, onApprove).disabled,
        visible: onApprove.visible,
          },
        ]
      : []),
      ...(onDetails
        ? [
            {
              id: "details",
              label: "Details",
              name: "Details",
              icon: (
                <Tooltip title="Details">
                  <InfoIcon sx={{ color: "#1976d2", width: 23 }} />
                </Tooltip>
              ),
              handler: ({ id }: ActionParams) => {
                onDetails.handler(id);
              },
              disabled: (row: any) =>
                getDisabledActionProps(row, onDetails).disabled,
              visible: onDetails.visible,
            },
          ]
        : []),
    ...(onReject
      ? [
          {
            id: "reject",
            label: "Reject",
            name: "Reject",
            icon: (
              <Tooltip title="Reject">
                <CloseIcon sx={{ color: "#d32f2f", width: 23 }} />
              </Tooltip>
            ),
            handler: ({ id }: ActionParams) => {
              onReject.handler(id);
            },
            disabled: (row: any) =>
              typeof onReject.isDisabled === "function"
                ? onReject.isDisabled(row)
                : onReject.isDisabled || false,
                visible: onReject.visible, 
          },
        ]
      : []),
  ];

  const delayedActions = actions.map((action) => ({
    ...action,
    handler: withMinWait(action.handler, 500),
  }));

  return delayedActions;
};

export default Actions;
